import { version } from '../../package.json'
import getBrowserName from './getBrowserName'
import getDeviceId from './getDeviceId'
import { HEIGHT, WIDTH } from './stage'
import { getDevice, isNo } from './utils'

export const APP_VERSION = version

export const APP_ENV = import.meta.env.VITE_ENV

export const COMMIT_HASH = import.meta.env.VITE_COMMIT_HASH || 'unknown'

export const COMMIT_NAME = import.meta.env.VITE_COMMIT_NAME || 'unknown'

export const DOMAIN = 'oylo.io'

export const AUTH_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN

export const AUTH_CLIENTID = import.meta.env.VITE_AUTH0_CLIENT_ID

export const AUTH_REDIRECT_URI = import.meta.env.VITE_AUTH0_REDIRECT_URI

export const AUTH_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE

const url = new URL(window.location.href)

export const APP_URL = import.meta.env.VITE_APP_URL

export const REMOTE_APP_URL = import.meta.env.VITE_REMOTE_APP_URL

export const API_URL = import.meta.env.VITE_API_URL

export const CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL

export const WEB_URL = import.meta.env.VITE_WEB_URL

// GCP project holding Cloud Logging for sessions. Local + dev builds point at oylo-dev;
// the prod project name is set via env at cutover.
export const GCP_PROJECT_ID = import.meta.env.VITE_GCP_PROJECT_ID || (APP_ENV === 'prod' ? 'oylo-prod' : 'oylo-dev')

// Google account for GCP console links (authuser=) so they don't open as Chrome's default account. Optional — when unset, console links carry no authuser param.
export const GCP_AUTHUSER = import.meta.env.VITE_GCP_AUTHUSER

export const CONTACT_EMAIL = import.meta.env.VITE_CONTACT_EMAIL || 'contact@oylo.io'

export const BASE_TITLE = 'oyλo'

export const QUERY = Object.fromEntries(url.searchParams.entries())

// Dev override of the phone's client mode (`src/lib/clientMode.js`): `?remote` or `?camera` wins over the saved choice and presence.
export const CLIENT_MODE_OVERRIDE = 'remote' in QUERY ? 'remote' : 'camera' in QUERY ? 'camera' : null

/*
 * `?as-user` simulates a normal (non-admin, non-dev) user. It suppresses admin elevation (see selectIsAdmin / selectIsTemplateCreator) AND suppresses SHOW_DEV_UI below, so dev-only UI (Templates, Effects, the commit-hash version string) and admin-only UI (Session type) are hidden — the honest view a real user gets. Useful for doc screenshots on this always-admin machine, and for normal-user debugging in general.
 *
 * It deliberately does NOT suppress IS_DEV capability (window.store, the sim overlay, devtools) — see the IS_DEV/SHOW_DEV_UI split below: the running app can render the honest normal-user chrome while keeping full programmatic instrumentation, which is what lets doc capture drive a real store + sim overlay under ?as-user.
 *
 * It deliberately does NOT touch IS_LOCAL: two IS_LOCAL-keyed infra sites (fetch credentials in store/api/base.js, the error-reporting handlers in index.jsx) must keep their local behaviour, or the very session you're capturing could break.
 *
 * Accepts an optional identity to display: `?as-user=alex@example.com`. The bare form `?as-user` uses AS_USER_DEFAULT_EMAIL. Only the DISPLAYED identity is replaced (see maskUser in store/auth/selectors.js) — never the token, never `sub`, so auth, persist keys and RTK Query are untouched. This is what keeps a real address out of published screenshots.
 */
export const AS_USER = 'as-user' in QUERY

/*
 * The stand-in identity every screenshot shows. `example.com` is RFC 2606 reserved, so it can never be a real address.
 */
export const AS_USER_DEFAULT_EMAIL = 'alex@example.com'
export const AS_USER_DEFAULT_NAME = 'Alex Rivera'

/*
 * The email to display under ?as-user. A bare `?as-user` (no value) yields an empty string from URLSearchParams, so fall back to the default rather than rendering a blank identity.
 */
export const AS_USER_EMAIL = (AS_USER && QUERY['as-user']) || AS_USER_DEFAULT_EMAIL

export const IS_DEBUG = 'debug' in QUERY

// One dockview instance is <main> (context/dock.md); ?classic renders the PanelStack columns instead.
export const IS_DOCK = !('classic' in QUERY)
// ?onecol — under the dock, one param per row on every param surface for this load (`selectParamColumn`).
export const ONE_COL = 'onecol' in QUERY

if (typeof document !== 'undefined' && IS_DOCK && document.documentElement) document.documentElement.dataset.dockUi = ''

// ?dev-status — dev-only: run the SessionStatus overlay through a simulated launch with no real session. Replaces the old ?sim flag and the mobile settings.dev_hints toggle.
export const DEV_STATUS = 'dev-status' in QUERY

export const IS_KIOSK = 'kiosk' in QUERY

export const AUTOPLAY = 'autoplay' in QUERY

export const SYNC_TEST = 'synctest' in QUERY

// ?first-time — force first-time / onboarding state on for testing. Replaces ?first, ?tutorial=1, and the TUTORIAL_DEV_MODE constant. Combined with the persisted flag in selectIsFirstTime.
export const IS_FIRST_TIME = 'first-time' in QUERY

export const AUTOSTART = isNo(QUERY.autostart) ? false : IS_KIOSK || 'autostart' in QUERY

export const OUTPUT_LAYER = QUERY.output_layer || null

export const IMPERSONATE = QUERY.impersonate || null

const ua = navigator.userAgent

export const IS_MOBILE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)

export const IS_ANDROID = /android/i.test(ua)

export const IS_IOS = /iphone|ipad|ipod/i.test(ua)

export const IS_FIREFOX =
	typeof window !== 'undefined' &&
	(navigator.userAgent.indexOf('Firefox') !== -1 ||
		(navigator.userAgent.indexOf('Gecko/') !== -1 && navigator.userAgent.indexOf('like Gecko') === -1))

export const IS_LOCAL = import.meta.env.MODE === 'development'

// Programmatic dev capability: window.store, the sim overlay (?dev-status), Pixi devtools, dev logging/invariants, env routing. Independent of ?as-user — an observed or captured session keeps full instrumentation even while showing normal-user chrome.
export const IS_DEV = IS_LOCAL || APP_ENV === 'dev' || 'dev' in QUERY

// Dev-only UI a normal user never sees (Templates, Effects, the commit-hash version string, dev settings rows, dev functional defaults). Session type is gated on `selectIsAdmin` instead. ?as-user suppresses ONLY this, so the running app renders exactly the honest normal-user view while IS_DEV capability stays live. This is the seam that lets doc capture drive a real store + sim overlay under ?as-user.
export const SHOW_DEV_UI = IS_DEV && !AS_USER

export const LOG_LEVEL = QUERY.log_level || (IS_LOCAL ? 'info' : 'warn')

export const OFFLINE = 'offline' in QUERY

export const IS_OUTPUT = window.location.pathname.includes('output')

export const IS_CAMERA = window.location.pathname.includes('/camera')

export const RTC_DIRECTION = IS_CAMERA ? 'sendonly' : IS_OUTPUT ? 'recvonly' : QUERY.direction || 'sendrecv'

export const DEVICE = getDevice()

export const BROWSER_NAME = getBrowserName()

export const DEVICE_NAME = `${QUERY.id || QUERY.name || `${BROWSER_NAME} / ${DEVICE}`}${IS_CAMERA ? ' [CAM]' : IS_OUTPUT ? ' [O]' : ''}`

export const DEVICE_ID = getDeviceId()

// Daemon installer downloads. The env is in the filename, not just the host, so a dev and a prod installer can sit side by side in ~/Downloads without one overwriting the other. Names must match what the daemon repo's ci.yml uploads (and orc's INSTALLER_FILENAME_PREFIX).
const DOWNLOAD_HOST = APP_ENV === 'prod' ? 'https://download-gcp.oylo.io' : 'https://download-gcp-dev.oylo.io'
const INSTALLER_NAME = APP_ENV === 'prod' ? 'setup-oylo' : 'setup-oylo-dev'

export const DAEMON_DOWNLOAD_URL = {
	windows: `${DOWNLOAD_HOST}/daemon/releases/windows/latest/${INSTALLER_NAME}.exe`,
	macos: `${DOWNLOAD_HOST}/daemon/releases/macos/latest/${INSTALLER_NAME}.pkg`,
}

// Falls back to the Windows build: macOS is the only other desktop target, and DEVICE reports the browser's OS rather than where the daemon will ultimately be installed.
export const DOWNLOAD_DAEMON_URL = DEVICE === 'macOS' ? DAEMON_DOWNLOAD_URL.macos : DAEMON_DOWNLOAD_URL.windows

function getTabId() {
	let tabId = sessionStorage.getItem('tabId')
	if (!tabId) {
		tabId = Math.random().toString(36).substring(2, 6)
		sessionStorage.setItem('tabId', tabId)
	}
	return tabId
}

export const TAB_ID = getTabId()

const TAB_SUFFIX = !IS_CAMERA && !IS_OUTPUT ? `:${TAB_ID}` : ''

export const ABLY_CLIENT_ID = `${DEVICE_ID}:${DEVICE_NAME}${IS_CAMERA ? ':camera' : IS_OUTPUT ? ':output' : TAB_SUFFIX}`

export { HEIGHT, WIDTH }
export { RATIO, STAGE_LEFT, STAGE_TOP } from './stage'

export const ABLY_API_KEY = import.meta.env.VITE_ABLY_API_KEY

// `?sim[=name]`: a session with no media server; the desktop tab hosts `src/lib/simServer.js` on this channel.
export const SIM = IS_DEV && 'sim' in QUERY

export const SIM_READY = 'sim_ready'

export const ABLY_SESSION_CHANNEL =
	QUERY.channel || (SIM ? `oylo_sim_${QUERY.sim || 'local'}` : import.meta.env.VITE_ABLY_CHANNEL)

export const SCTP_CAUSE_CODES = [
	'No SCTP error',
	'Invalid stream identifier',
	'Missing mandatory parameter',
	'Stale cookie error',
	'Sender is out of resource (i.e., memory)',
	'Unable to resolve address',
	'Unrecognized SCTP chunk type received',
	'Invalid mandatory parameter',
	'Unrecognized parameters',
	'No user data (SCTP DATA chunk has no data)',
	'Cookie received while shutting down',
	'Restart of an association with new addresses',
	'User-initiated abort',
	'Protocol violation',
]

export const SIGNALING_STATES = {
	STABLE: 'stable',
	HAVE_LOCAL_OFFER: 'have-local-offer',
	HAVE_REMOTE_OFFER: 'have-remote-offer',
	HAVE_LOCAL_PRANSWER: 'have-local-pranswer',
	HAVE_REMOTE_PRANSWER: 'have-remote-pranswer',
	CLOSED: 'closed',
}

export const CONNECTION_STATUS = {
	NEW: 'new',
	INITIALIZED: 'initialized',
	CONNECTING: 'connecting',
	CONNECTED: 'connected',
	DISCONNECTED: 'disconnected',
	SUSPENDED: 'suspended',
	CLOSING: 'closing',
	CLOSED: 'closed',
	FAILED: 'failed',
}

export const MAX_SAFE_INTEGER = 100000000

export const SESSION_STATUS = {
	// real statuses
	CREATED: 'created',
	// orc accepted the launch request; the actual GCE insert runs async (no ~5-9s blocking POST). Earliest launch state — precedes LAUNCHED ('insert accepted'). Returned in the POST /sessions body.
	LAUNCH_REQUESTED: 'launch_requested',
	MIGRATING: 'migrating',
	LAUNCHED: 'launched',
	PROVISIONING: 'provisioning',
	PENDING: 'pending',
	STARTED: 'started',
	RUNNING: 'running',
	STOPPING: 'stopping',
	FINISHED: 'finished', // -> idle
	// my statuses
	IDLE: 'idle',
	LOADING: 'loading',
}

export const SESSION_STATUSES = Object.values(SESSION_STATUS)

// Launch SUBPHASES within the 'provisioning' status. orc forwards these (status='provisioning' + phase) as pure FE-progress details; they never change session_status. Ordered by the sequence they arrive in.
export const SESSION_PHASE = {
	// Under 'launch_requested', on orc's 'launching' message: GCE granted the GPU and the VM is provisioning, but no VM is elected yet.
	GPU_FOUND: 'gpu_found',
	MACHINE_STARTING: 'machine_starting',
	APP_STARTING: 'app_starting',
	LOADING_MODELS: 'loading_models',
}

// The real progress axis on GCP is NOT the status axis (launch parks at launch_requested for the whole VM hunt, then all provisioning work rides sub-phases under a single 'provisioning' status). A "stage" is the derived progress step: the phase when status is 'provisioning', otherwise the status itself. This is the key into PROGRESS_MAP and the axis the progress bar advances along. Ordered; used to reject backward moves.
export const PROGRESS_STAGE_ORDER = [
	SESSION_STATUS.LAUNCH_REQUESTED,
	SESSION_PHASE.GPU_FOUND,
	SESSION_PHASE.MACHINE_STARTING,
	SESSION_PHASE.APP_STARTING,
	SESSION_PHASE.LOADING_MODELS,
	SESSION_STATUS.STARTED,
	SESSION_STATUS.RUNNING,
]

// Derive the progress stage from a (status, phase) pair. Phase only carries meaning under 'provisioning'. On a reload mid-provisioning the DB has no phase (orc never persists it), so default 'provisioning' (phase-less) to the first phase band — the bar resumes at machine_starting instead of falling to 0.
export const getProgressStage = (status, phase) => {
	if (status === SESSION_STATUS.PROVISIONING) return phase || SESSION_PHASE.MACHINE_STARTING
	if (status === SESSION_STATUS.LAUNCH_REQUESTED && phase === SESSION_PHASE.GPU_FOUND) return phase
	return status
}

export const STARTING_STATUSES = [
	SESSION_STATUS.CREATED,
	SESSION_STATUS.LAUNCH_REQUESTED,
	SESSION_STATUS.LAUNCHED,
	SESSION_STATUS.PENDING,
	SESSION_STATUS.PROVISIONING,
	SESSION_STATUS.MIGRATING,
	SESSION_STATUS.STARTED,
]

export const ACTIVE_SESSION_STATUS = [
	SESSION_STATUS.CREATED,
	SESSION_STATUS.LAUNCH_REQUESTED,
	SESSION_STATUS.LAUNCHED,
	SESSION_STATUS.PENDING,
	SESSION_STATUS.PROVISIONING,
	SESSION_STATUS.MIGRATING,
	SESSION_STATUS.STARTED,
	SESSION_STATUS.RUNNING,
]

export const DC_MESSAGES = {
	PARAMETERS: 'parameters',
	// The runner's loop samples, `{ <live path>: value }`, each message the whole set. The server renders them and relays them for display; nobody stores them.
	SAMPLES: 'samples',
	// The session's musical clock, `tempo.state()` plus `at`, the sender's wall clock. The server keeps the last and replays it to a joining peer; `null` asks that peer to seed it.
	TEMPO: 'tempo',
	PRESENCE: 'presence',
	SESSION_STATE: 'session_state',
	// The session's shared playback keys (`sharedPlayback`), as a diff. The server merges them and replays the whole to a joining peer; only the runner carries `playing` out.
	PLAYBACK: 'playback',
	// A preset load by index for the other clients to follow; relayed, never stored.
	CUE: 'cue',
	// A desktop asking to run automation and autoplay here.
	TAKE_RUNNER: 'take_runner',
	ERROR: 'error',
	SHUTDOWN: 'shutdown',
	SET_ACTIVE: 'set_active_name', // deprecated alias
	SET_SOURCE: 'set_active_name',
	SEND_VIDEO: 'send_video',
	SET_CONTROL_MODE: 'set_control_mode',
	VIDEO_QUALITY: 'video_quality',
	SCHEME: 'scheme',
	ORIENTATION: 'orientation',
	// Once per second: the browser's uplink view and downlink playback metrics (src/lib/clientStats.js).
	CLIENT_STATS: 'client_stats',
	// From the server: why quality changed and what it did (src/lib/networkNotice.js).
	NETWORK_QUALITY: 'network_quality',
	SOURCE_QUALITY: 'source_quality',
	SET_TARGET_FPS: 'set_target_fps',
	// Ask the server for a full parameter snapshot. The reply is an ordinary PARAMETERS message.
	RESYNC: 'resync',
	// Desktop link relay: send `{ to, msg }`, receive `{ from, msg }`. A remote's goes to the runner, the runner's to the named remote; nothing stored (`src/lib/desktopLink/`).
	LINK: 'link',
	// `{ image_id }`, a client UUID: the server saves its next real frame and answers over Ably `image_status`.
	CAPTURE_IMAGE: 'capture_image',
}

export const DEVICE_TYPES = {
	DESKTOP: 'desktop',
	MOBILE: 'mobile',
	DAEMON: 'daemon',
	CAMERA: 'camera',
	SCREEN: 'screen',
	REMOTE: 'remote',
}

export const RECORDING_STATUS = {
	REQUESTED: 'recording_requested',
	STARTED: 'recording_started',
	STOP_REQUESTED: 'recording_stop_requested',
	ENDED: 'recording_ended',
	MERGING: 'recording_merging',
	MERGED: 'recording_merged',
	FAILED: 'recording_failed',
}

export const RECORDING_STATUS_MAP = {}

for (const key in RECORDING_STATUS) {
	RECORDING_STATUS_MAP[RECORDING_STATUS[key]] = key
}

export const ABLY_MESSAGES = {
	STATUS_UPDATE: 'statusUpdate',
	// orc's capacity-hunt progress `{ sessionId, phase, timestamp }`; never a status change (status stays launch_requested).
	LAUNCHING: 'launching',
	ANSWER: 'answer',
	OFFER: 'offer',
	OFFER_ERROR: 'offer_error',
	INVALIDATE_API_TAGS: 'invalidate_api_tags',
	SYNC_ACTION: 'sync_action',
	// An output window with no project asks the runner for the project's output mapping; it answers as a SYNC_ACTION.
	OUTPUT_MAPPING_REQUEST: 'output_mapping_request',
	// The runner's map editor mouse in output px, `[x, y] | null`, for the cross on `/output`.
	MAP_CURSOR: 'map_cursor',
	PRESENCE_ENTER: 'enter',
	PRESENCE_LEAVE: 'leave',
	PRESENCE_UPDATE: 'update',
	RECORDING_STATUS: 'recording_status',
	// Direct desktop link signaling `{ from, to, sdp | candidate }`; the server ignores them (`src/lib/desktopLink/`).
	LINK_OFFER: 'link_offer',
	LINK_ANSWER: 'link_answer',
	LINK_ICE: 'link_ice',
	// A client saw its first user input of the session; every other client ends its idle timer (`src/lib/sessionActivity.js`).
	USER_ACTIVITY: 'user_activity',
	IMAGE_STATUS: 'image_status',
}

export const CONTEXTS = {
	ASSET: 'asset',
	ASSET_PICKER: 'asset_picker',
	EXAMPLE_PRESET: 'example_preset',
	// The Examples folder's header in the Presets panel.
	EXAMPLES: 'examples',
	IMAGE: 'image',
	LAYER: 'layer',
	OBJECT: 'object',
	PRESET: 'preset',
	PROJECT: 'project',
	RECORDING: 'recording',
	SOURCE: 'source',
	SOURCE_PRESET: 'source_preset',
	STAGE: 'stage',
	TEMPLATE: 'template',
}

export const INPUT_TEXT_REG = /[^a-zA-Z0-9\-_!@#$%^&*().,+:'?<>{}|" ]/g

export const INPUT_MAX_LENGTH = 36

export const MIN_CREDIT_TO_START = 10

export const LOW_CREDITS_WARNING_THRESHOLD = IS_DEV ? (QUERY.low_credits ? parseInt(QUERY.low_credits) : 15) : 15

export const STORAGE_KEY = 'startup_state'

export const THUMBNAIL_SIZE = parseInt(import.meta.env.VITE_THUMBNAIL_SIZE) || 320
export const THUMBNAIL_MIME = 'image/webp'
export const THUMBNAIL_QUALITY = 0.8

export const EMPTY_OBJECT = {}

export const EMPTY_ARRAY = []
Object.freeze(EMPTY_ARRAY)

export const EMPTY_SESSION = {
	status: SESSION_STATUS.IDLE,
}
Object.freeze(EMPTY_SESSION)

export const EMPTY_IMAGE =
	'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgAB/ax5LIAAAAAASUVORK5CYII='

export const CAMERA_WIDTH = parseInt(QUERY.cw) || parseInt(import.meta.env.VITE_CAMERA_WIDTH) || 1280

export const CAMERA_HEIGHT = parseInt(QUERY.ch) || parseInt(import.meta.env.VITE_CAMERA_HEIGHT) || 720

// WebRTC Encoding Parameters contentHint: 'motion' (default), 'detail', 'text'. Motion: under bandwidth pressure the encoder keeps frame rate and gives up detail, which suits a live camera feeding a diffusion pipeline.
export const CONTENT_HINT = QUERY.content_hint || 'motion'

// maxBitrate in KBPS (socket.js converts to bps). Recommended range: 500 - 50000.
export const MAX_BITRATE = parseInt(QUERY.max_bitrate) || (IS_MOBILE ? 3000 : 8000)

// minBitrate: Minimum bitrate floor to prevent bandwidth estimator from dropping too low during still scenes, which causes FPS drops when motion resumes.
export const MIN_BITRATE = parseInt(QUERY.min_bitrate) || (IS_MOBILE ? 500 : 1000)

// Target playout delay of the downlink video, in ms (RTCRtpReceiver.jitterBufferTarget). A little delay buys fewer freezes on a jittery link. Placeholder until measured (E2 in imagine_srv docs/BWE_OVERHAUL.md).
export const JITTER_BUFFER_TARGET_MS = parseInt(QUERY.jitter_buffer_ms) || 60

// On an ICE drop, try an ICE restart on the same peer connection before a full reconnect. `?ice_restart=0` turns it off.
export const ICE_RESTART = QUERY.ice_restart !== '0'

// maxFramerate: a static encoder backstop, deliberately far above any rate the pipeline produces. The user-facing outgoing rate is settings.fps, enforced by the enqueue gate in useBackgroundStream — leaving this loose keeps Chrome's rate-control out of that decision, so a drop is always attributable to the gate or to qualityLimitationReason, never to an ambiguous third dropper.
export const MAX_FRAMERATE = parseInt(QUERY.max_framerate) || 60

// The fixed rate the capture pipeline and the GSAP ticker run at. Not user-facing: settings.fps caps what leaves the enqueue gate, it does not change how fast frames are produced or how fast tweens advance.
export const CAPTURE_FPS = 30

// Every throttle on the parameters path shares this period, so the three senders (live sync, inpaint mask, the socket's generic debounceDC) cannot drift to different rates. Each sender diffs against its own last-sent baseline at send time, so a dropped intermediate call still carries its changes in the trailing send.
//
// 33ms is a MINIMUM spacing, not a target rate: `throttle` measures the next period from the actual invocation, so scheduling jitter puts the effective rate a few Hz below the nominal one (~26Hz measured for 30 nominal) and it never catches back up.
export const PARAM_SEND_MS = Math.round(1000 / 30)

// The runner role's grace across a DC drop: the server's `RUNNER_GRACE_S` (`imagine_srv/imagine/session/webrtc_manager.py`), kept equal by hand.
export const RUNNER_GRACE_MS = 20_000

// How near an end a scrub counts as being AT it — read by `progressAt` (the paint) and `releaseScrub` (the settle).
//
// ⚠️ ONE constant because the two must AGREE. Declared separately they can drift silently, and nothing fails at the seam: the bar snaps shut on a fade the settle then refuses to finish, which parks it forever and blocks autoplay behind it.
//
// ⚠️ An exact test is not an option. A list row's right edge is flush with the viewport's, so the pointer x mapping to exactly 1 is one column past anything the browser can report — a measured full-width row tops out at 0.9975. Wide enough to cover that, far narrower than any deliberate park.
export const SCRUB_END_EPSILON = 0.02

// scaleResolutionDownBy: Resolution scaling factor (default: 1.0 = no scaling) Must be >= 1.0. 2.0 = 50% width/height (25% total pixels)
export const SCALE_RESOLUTION_DOWN_BY = parseFloat(QUERY.scale_resolution_down_by) || 1.0

// degradationPreference: How to handle bandwidth constraints Options: 'maintain-framerate', 'maintain-resolution', 'balanced' (default)
export const DEGRADATION_PREFERENCE = QUERY.degradation_preference || 'maintain-resolution'

// priority: Encoding priority Options: 'very-low', 'low', 'medium', 'high' (default)
export const ENCODING_PRIORITY = QUERY.encoding_priority || 'high'

// Audio Encoding Parameters Audio bitrate in bits per second (default: 128 kbps)
export const AUDIO_BITRATE = parseInt(QUERY.audio_bitrate) || 128000

if (IS_DEV) {
	window.info = {
		API_URL,
		APP_VERSION,
		CDN_BASE_URL,
		APP_URL,
		AUTH_DOMAIN,
		AUTH_AUDIENCE,
		COMMIT_HASH,
		COMMIT_NAME,
		ABLY_CLIENT_ID,
		ABLY_SESSION_CHANNEL,
		AUTOPLAY,
		AUTOSTART,
		BROWSER_NAME,
		CAMERA_WIDTH,
		CONTACT_EMAIL,
		DEVICE,
		DEVICE_ID,
		DEVICE_NAME,
		DEV_STATUS,
		HEIGHT,
		CLIENT_MODE_OVERRIDE,
		IS_DEBUG,
		IS_DEV,
		SHOW_DEV_UI,
		IS_FIRST_TIME,
		IS_KIOSK,
		IS_MOBILE,
		IS_CAMERA,
		IS_OUTPUT,
		WIDTH,
	}
}
